import { useState, useEffect, useCallback } from 'react';
import { Form, message } from 'antd';
import {
  getSlideData,
  deleteAnnotation,
  patchAnnotation,
  getPhotoData,
  deleteImageAnnotation,
  updateImageAnnotation,
  getGrossAnnotationList,
  deleteGrossAnnotation,
  updateGrossAnnotation,
} from '../api/slideAPI';
import { checkUserName } from '../api/authAPI';
import { hasTempViewerSession } from '../api/api';
import { ensureTempViewerPassword, getStoredTempViewerName } from '../lib/tempViewer';

interface AnnotationInfoHookProps {
  requirePasswordOnEdit?: boolean;
  currentSlide: any;
  setCurrentSlide: (data: any) => void;
  skipRefresh?: boolean;
  customAnnotations?: any[];
  isImageMode?: boolean;
  isGrossMode?: boolean;
  photoId?: number;
  grossPhotoId?: number;
  onAnnotationsRefresh?: (annotations: any[]) => void;
  viewerContainerId?: string;
}

/**
 * @ref 기존 Openseadragon 뷰어가 슬라이드만 고려했기에 [currentSlide, setCurrentSlide] 이 값이 OSD 뷰어에 표시하는 데이터를 전부 총칭하는 중 (이미지도 갑자기 어노테이션 해야해서...)
 * @ref 이미지 - setCurrentSlide({annotation: data}) / 슬라이드 - setCurrentSlide(data) 처럼 구조가 약간 다름 (API 리턴 값 형태가 약간 다름)
 */
export const useAnnotationInfo = ({
  currentSlide,
  setCurrentSlide,
  skipRefresh = false,
  customAnnotations,
  isImageMode = false,
  isGrossMode = false,
  photoId,
  grossPhotoId,
  requirePasswordOnEdit = false,
  onAnnotationsRefresh,
  viewerContainerId = 'openseadragon',
}: AnnotationInfoHookProps) => {
  const [selectedAnnotation, setSelectedAnnotation] = useState<any>(null);
  const [annotationInfoOpen, setAnnotationInfoOpen] = useState(false);
  const [infoPanelPosition, setInfoPanelPosition] = useState({ x: 0, y: 0 });
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentColor, setCurrentColor] = useState<string>('#285F3D');
  const [annotationForm] = Form.useForm();
  const isTempViewer = hasTempViewerSession();
  const requiresEditPassword = Boolean(requirePasswordOnEdit && selectedAnnotation?.anonymous && !isImageMode);

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordModalAction, setPasswordModalAction] = useState<'delete' | null>(null);
  const [passwordInput, setPasswordInput] = useState("");

  // 어노테이션 클릭 핸들러
  const handleAnnotationClick = useCallback(async (event: any) => {
    let target = event.target;
    let annotationId = target.id;

    if (!annotationId && target.parentElement) {
      target = target.parentElement;
      annotationId = target.id;
    }

    if (annotationId && annotationId.startsWith('annotation')) {
      event.stopPropagation();

      const id = parseInt(annotationId.replace('annotation', ''));

      let annotation;

      if (!skipRefresh) {
        let refreshedData;
        if (isGrossMode && grossPhotoId) {
          refreshedData = await getGrossAnnotationList(grossPhotoId);
          setCurrentSlide({ annotations: refreshedData });
          annotation = refreshedData?.find(
            (anno: any) => (anno.antnGrossPhotoId || anno.annoId) === id
          );
        } else if (isImageMode && photoId) {
          refreshedData = await getPhotoData(photoId);
          setCurrentSlide({ annotations: refreshedData });
          annotation = refreshedData?.find(
            (anno: any) => (anno.antnPhotoId || anno.annoId) === id
          );
        } else if (currentSlide?.slideId) {
          refreshedData = await getSlideData(currentSlide.slideId);
          setCurrentSlide({ ...currentSlide, ...refreshedData });
          annotation = refreshedData?.annotations?.find(
            (anno: any) => anno.annoId === id
          );
        }
      }

      if (!annotation) {
        const annotationsToSearch = customAnnotations || currentSlide?.annotations || [];
        annotation = annotationsToSearch.find((anno: any) => {
          if (isGrossMode) return (anno.antnGrossPhotoId || anno.annoId) === id;
          if (isImageMode) return (anno.antnPhotoId || anno.annoId) === id;
          return anno.annoId === id;
        });
      }

      if (annotation) {
        const viewerContainer = document.getElementById(viewerContainerId);
        if (viewerContainer) {
          const containerRect = viewerContainer.getBoundingClientRect();
          const x = event.clientX - containerRect.left + 50;
          const infoPanelHeight = 150;
          const y = event.clientY - containerRect.top - (infoPanelHeight / 3);
          setInfoPanelPosition({ x, y });
        }

        const annoInfo = annotation.annoInfo;
        let initialColor = '#285F3D';
        if (annoInfo && annoInfo[0] === 'g' && annoInfo[1]?.class === 'annotation arrow-group') {
          initialColor = annoInfo[1]?.stroke || '#285F3D';
        } else if (annoInfo && annoInfo[1]) {
          initialColor = annoInfo[1]?.stroke || '#285F3D';
        }
        setCurrentColor(initialColor);

        setSelectedAnnotation(annotation);
        setAnnotationInfoOpen(true);
      } else {
        console.error('Annotation not found:', id);
        console.log('Available annotations:', customAnnotations || currentSlide?.annotations);
      }
    }
  }, [currentSlide, setCurrentSlide, skipRefresh, customAnnotations, isImageMode, photoId, viewerContainerId]);

  const getCurrentColor = useCallback(() => {
    return currentColor;
  }, [currentColor]);

  const getShapeType = useCallback((annotation: any) => {
    const annoInfo = annotation?.annoInfo;
    if (!annoInfo) return "";

    const shapeType = annoInfo[0];
    if (shapeType === 'g' && annoInfo[1]?.class === 'annotation arrow-group') {
      return "DrawArrow";
    } else if (shapeType === 'rect') {
      return "Rect";
    } else if (shapeType === 'ellipse') {
      return "DrawEllipse";
    } else if (shapeType === 'path') {
      return "Draw";
    } else if (shapeType === 'line') {
      return "Ruler";
    }
    return "";
  }, []);

  const changeColor = useCallback((color: string) => {
    setCurrentColor(color);
    annotationForm.setFieldsValue({ color });
  }, [annotationForm]);

  const handleDeleteAnnotation = useCallback(async (targetAnnotation?: any) => {
    const annotationToDelete = targetAnnotation || selectedAnnotation;
    if (!annotationToDelete) return;

    const writer = isTempViewer ? getStoredTempViewerName() : await checkUserName();
    if (writer !== annotationToDelete.writer) {
      window.alert("삭제 권한이 없습니다.");
      return;
    }

    if (!isImageMode && annotationToDelete.anonymous) {
      setPasswordModalAction('delete');
      setPasswordInput("");
      setPasswordModalOpen(true);
      return;
    }

    if (window.confirm(`해당 어노테이션을 삭제하시겠습니까?`)) {
      if (isGrossMode && grossPhotoId) {
        await deleteGrossAnnotation(annotationToDelete.antnGrossPhotoId || annotationToDelete.annoId);
        const data = await getGrossAnnotationList(grossPhotoId);
        setCurrentSlide({ annotations: data });
      } else if (isImageMode && photoId) {
        await deleteImageAnnotation(annotationToDelete.annoId);
        const data = await getPhotoData(photoId);
        setCurrentSlide({ annotations: data });
      } else {
        await deleteAnnotation(annotationToDelete.annoId, undefined);
        const data = await getSlideData(currentSlide.slideId);
        setCurrentSlide({ ...currentSlide, ...data });
        onAnnotationsRefresh?.(data.annotations ?? []);
      }
      setAnnotationInfoOpen(false);
      setSelectedAnnotation(null);
    }
  }, [selectedAnnotation, currentSlide, setCurrentSlide, isImageMode, photoId, isTempViewer, onAnnotationsRefresh]);

  const handleEditAnnotation = useCallback(async () => {
    if (!selectedAnnotation) return;

    const writer = isTempViewer ? getStoredTempViewerName() : await checkUserName();
    if (writer !== selectedAnnotation.writer) {
      window.alert("수정 권한이 없습니다.");
      return;
    }

    annotationForm.setFieldsValue({
      content: selectedAnnotation.content,
      anonymousPassword: "",
    });
    setShowEditModal(true);
  }, [selectedAnnotation, isTempViewer, annotationForm]);

  const submitEditAnnotation = useCallback(async (values: any) => {
    if (!selectedAnnotation) return;

    const annoInfo = selectedAnnotation.annoInfo;
    let newAnnoInfo;

    if (annoInfo[0] === 'g' && annoInfo[1].class === 'annotation arrow-group') {
      const newColor = values.color || annoInfo[1].stroke;
      newAnnoInfo = [
        annoInfo[0],
        {
          ...annoInfo[1],
          stroke: newColor,
        },
        ...(annoInfo.slice(2).map((child: any) => {
          if (Array.isArray(child) && child[1]) {
            return [
              child[0],
              {
                ...child[1],
                stroke: newColor,
              }
            ];
          }
          return child;
        }))
      ];
    } else {
      newAnnoInfo = [
        annoInfo[0],
        {
          ...annoInfo[1],
          stroke: values.color || annoInfo[1].stroke,
        },
      ];
    }

    const updatedColor = values.color || annoInfo[1]?.stroke || '#285F3D';
    setCurrentColor(updatedColor);

    let data;
    if (isGrossMode && grossPhotoId) {
      await updateGrossAnnotation(
        selectedAnnotation.antnGrossPhotoId || selectedAnnotation.annoId,
        newAnnoInfo,
        values.content
      );
      data = await getGrossAnnotationList(grossPhotoId);
      setCurrentSlide({ annotations: data });

      const annotationId = selectedAnnotation.antnGrossPhotoId || selectedAnnotation.annoId;
      const updatedAnnotation = data?.find(
        (anno: any) => (anno.antnGrossPhotoId || anno.annoId) === annotationId
      );
      if (updatedAnnotation) {
        setSelectedAnnotation(updatedAnnotation);
      }
    } else if (isImageMode && photoId) {
      await updateImageAnnotation(
        selectedAnnotation.antnPhotoId || selectedAnnotation.annoId,
        newAnnoInfo,
        values.content
      );
      data = await getPhotoData(photoId);
      setCurrentSlide({ annotations: data });

      const annotationId = selectedAnnotation.antnPhotoId || selectedAnnotation.annoId;
      const updatedAnnotation = data?.find(
        (anno: any) => (anno.antnPhotoId || anno.annoId) === annotationId
      );
      if (updatedAnnotation) {
        setSelectedAnnotation(updatedAnnotation);
      }
    } else {
      const anonymousPassword = values.anonymousPassword?.trim() || null;
      const needsPassword = isTempViewer || requiresEditPassword || selectedAnnotation?.anonymous;
      if (needsPassword && !anonymousPassword) return;

      const result = await patchAnnotation(
        selectedAnnotation.annoId,
        newAnnoInfo,
        values.content,
        anonymousPassword ? { anonymousPassword } : undefined
      );
      if (result === false || (result && typeof result === 'object' && Object.keys(result).length === 0)) {
        message.error("비밀번호가 일치하지 않아 수정에 실패했습니다.");
        return;
      }
      data = await getSlideData(currentSlide.slideId);

      setCurrentSlide({ ...currentSlide, ...data });
      onAnnotationsRefresh?.(data.annotations ?? []);

      const updatedAnnotation = data.annotations?.find(
        (anno: any) => anno.annoId === selectedAnnotation.annoId
      );
      if (updatedAnnotation) {
        setSelectedAnnotation(updatedAnnotation);
      }
    }

    message.success("어노테이션이 수정되었습니다.");
    setShowEditModal(false);
  }, [selectedAnnotation, currentSlide, setCurrentSlide, isImageMode, photoId, isTempViewer, onAnnotationsRefresh, requiresEditPassword]);

  const handlePasswordConfirm = useCallback(async () => {
    if (!selectedAnnotation || !passwordModalAction) return;
    if (!passwordInput.trim()) {
      message.warning("비밀번호를 입력해주세요.");
      return;
    }

    if (passwordModalAction === 'delete') {
      const result = await deleteAnnotation(selectedAnnotation.annoId, passwordInput);
      if (!result) {
        message.error("비밀번호가 올바르지 않거나 삭제에 실패했습니다.");
        return;
      }
      message.success("어노테이션이 삭제되었습니다.");
      setPasswordModalOpen(false);
      setPasswordModalAction(null);
      setPasswordInput("");
      const data = await getSlideData(currentSlide.slideId);
      setCurrentSlide({ ...currentSlide, ...data });
      onAnnotationsRefresh?.(data.annotations ?? []);
      setAnnotationInfoOpen(false);
      setSelectedAnnotation(null);
    }
  }, [selectedAnnotation, passwordModalAction, passwordInput, currentSlide, setCurrentSlide, onAnnotationsRefresh, annotationForm]);

  const closeAnnotationInfo = useCallback(() => {
    setAnnotationInfoOpen(false);
    setSelectedAnnotation(null);
  }, []);

  useEffect(() => {
    if (selectedAnnotation) {
      const annoInfo = selectedAnnotation.annoInfo;
      let initialColor = '#285F3D';
      if (annoInfo && annoInfo[0] === 'g' && annoInfo[1]?.class === 'annotation arrow-group') {
        initialColor = annoInfo[1]?.stroke || '#285F3D';
      } else if (annoInfo && annoInfo[1]) {
        initialColor = annoInfo[1]?.stroke || '#285F3D';
      }
      setCurrentColor(initialColor);
    }
  }, [selectedAnnotation]);

  useEffect(() => {
    if (showEditModal && selectedAnnotation) {
      annotationForm.setFieldsValue({
        content: selectedAnnotation.content,
        color: currentColor,
      });
    }
  }, [showEditModal, selectedAnnotation, annotationForm, currentColor]);

  useEffect(() => {
    if (!annotationInfoOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showEditModal || passwordModalOpen) return;

      let checkTarget: HTMLElement | null = target;
      while (checkTarget) {
        if (checkTarget.id?.startsWith('annotation')) {
          return;
        }
        checkTarget = checkTarget.parentElement;
      }

      if (!target.closest('.annotation-info-panel') && !target.closest('svg')) {
        setAnnotationInfoOpen(false);
        setSelectedAnnotation(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [annotationInfoOpen, showEditModal, passwordModalOpen]);

  return {
    selectedAnnotation,
    annotationInfoOpen,
    infoPanelPosition,
    showEditModal,
    annotationForm,
    requiresEditPassword,
    passwordModalOpen,
    passwordModalAction,
    passwordInput,

    handleAnnotationClick,
    handleDeleteAnnotation,
    handleEditAnnotation,
    submitEditAnnotation,
    closeAnnotationInfo,
    setShowEditModal,
    setSelectedAnnotation,
    setAnnotationInfoOpen,
    setInfoPanelPosition,
    setPasswordModalOpen,
    setPasswordInput,
    handlePasswordConfirm,

    getCurrentColor,
    getShapeType,
    changeColor,
  };
};
